import { FC, useEffect, useState } from 'react'

import { useNavigate } from '@tanstack/react-router'
import { COMPANIES_PATH } from 'commons/constants/routes'
import { CompanyDocumentsWebPage, Document } from 'commons/types/DTO/bank/companyDetails/document'
import { TableDataQa } from 'commons/types/enums'
import { Layer, PageSectionPrimary, Table } from 'ui/components'
import { ButtonGroup } from 'ui/components/ButtonGroup'
import { Button } from 'ui/components/Buttons'

import { handlerServerError } from '@orbit/errors'

import { CompanyDocumentsTableProps } from './CompanyDocumentsTableProps'
import { columns } from './columns'
import { Filter } from './components/Filter'
import { useComponentsEssence } from './hooks/useComponentsEssence'
import { useFilterCompanyDocumentsWebPage } from './hooks/useFilterCompanyDocumentsWebPage'

export const CompanyDocumentsTable: FC<CompanyDocumentsTableProps> = (props) => {
  const { data } = props
  const { id } = data
  const navigate = useNavigate()
  const returnUrl = `${COMPANIES_PATH}/${id}#documents`

  const [webPageData, setWebPageData] = useState<CompanyDocumentsWebPage>({
    documentsAlertsOptions: data.documentsAlertsOptions,
    documentsFrequencyOptions: data.documentsFrequencyOptions,
    documentsInternalOptions: data.documentsInternalOptions,
    documentsSubjectTypeOptions: data.documentsSubjectTypeOptions,
    form: data.form,
    documents: data.documents,
    documentsTotalCount: data.documentsTotalCount
  })

  useEffect(() => {
    setWebPageData({
      documentsAlertsOptions: data.documentsAlertsOptions,
      documentsFrequencyOptions: data.documentsFrequencyOptions,
      documentsInternalOptions: data.documentsInternalOptions,
      documentsSubjectTypeOptions: data.documentsSubjectTypeOptions,
      form: data.form,
      documents: data.documents,
      documentsTotalCount: data.documentsTotalCount
    })
  }, [
    data.documents,
    data.documentsTotalCount,
    data.form,
    data.documentsSubjectTypeOptions,
    data.documentsInternalOptions,
    data.documentsFrequencyOptions,
    data.documentsAlertsOptions
  ])

  const { routine: filterCompanyDocumentsWebPage, isLoading } = useFilterCompanyDocumentsWebPage({
    onSuccess: async (_, result) => {
      setWebPageData(result)
    },
    onError: (error) => handlerServerError(error)
  })

  const { tableState, filterForm, changePagination, onSort, DeleteConfirmation, onDeleteDocument, clearFilter } =
    useComponentsEssence(id, filterCompanyDocumentsWebPage, webPageData)

  return (
    <PageSectionPrimary
      title="Documents"
      anchor="documents"
      actions={
        <ButtonGroup margin="small">
          <Button
            face="tertiary"
            onClick={() =>
              navigate({
                to: '/documents/new',
                search: {
                  subjectType: 'company',
                  companyId: id,
                  subjectName: data.companyName,
                  returnUrl: returnUrl
                }
              })
            }
          >
            Add New
          </Button>
        </ButtonGroup>
      }
      face="outlined"
    >
      <DeleteConfirmation />
      <Layer rounded shadowed stretch>
        <Filter
          form={filterForm}
          expanded={tableState.expanded}
          onToggle={tableState.onFilterToggle}
          data={data}
          onClear={clearFilter}
        />
        <Table<Document>
          dataQa={TableDataQa.BP_COMPANY_DOCUMENTS}
          columns={columns(onDeleteDocument, returnUrl)}
          dataSource={webPageData.documents.map((el) => ({
            item: el
          }))}
          loading={isLoading}
          pagination={{
            page: tableState.page,
            size: tableState.size,
            onChange: changePagination,
            total: webPageData.documentsTotalCount
          }}
          sorting={{ order: tableState.order || [], onChange: (value) => onSort(value.direction, value.field) }}
        />
      </Layer>
    </PageSectionPrimary>
  )
}
